import ScrollMagic from 'scrollmagic';
import { classes } from '../../constants';
import { handleNavChange } from '../../modules/product-nav';
import { debounce } from '../../utils/debounce';

var controller = new ScrollMagic.Controller();
/**
 * Class definition for `clover-accordion-heading`
 *
 * @class CloverAccordionHeading
 * @extends {HTMLElement}
 */
class CloverAccordionHeading extends HTMLElement {
  constructor(self) {
    self = super(self);
    this.setAttribute('role', 'tab');
    this.setAttribute('tabindex', -1);
    this.setAttribute('data-js-auto-open', true);
    this._setupHeading();
    return self;
  }

  _setupHeading() {
    const iconClasses = this.getAttribute('data-js-icon-classes');
    const tabIcon = document.createElement('div');
    const icon = document.createElement('i');

    icon.setAttribute('aria-hidden', true);
    if (iconClasses) {
      icon.classList.add('icon', ...iconClasses.split(' '));
    } else {
      icon.classList.add('icon');
    }
    icon.setAttribute('data-grunticon-embed');

    tabIcon.classList.add('accordion__tab-icon');
    tabIcon.appendChild(icon);

    this.appendChild(tabIcon);
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'clover-accordion-heading';
  }

  /**
   * Watched attributes
   *
   * @readonly
   * @static
   */
  static get observedAttributes() {
    return ['selected', 'expanded'];
  }

  /**
   * Getter for `selected` attribute
   */
  get selected() {
    return this.hasAttribute('selected');
  }

  /**
   * Setter for `selected` attribute
   */
  set selected(value) {
    if (Boolean(value)) {
      this.setAttribute('selected', '');
    } else {
      this.removeAttribute('selected');
    }
  }

  /**
   * Getter for `expanded` attribute
   */
  get expanded() {
    return this.hasAttribute('expanded');
  }

  /**
   * Setter for `expanded` attribute
   */
  set expanded(value) {
    if (Boolean(value)) {
      this.setAttribute('expanded', '');
    } else {
      this.removeAttribute('expanded');
    }
  }

  /**
   * Lifecycle event when element is attached to DOM
   */
  connectedCallback() {
    this._setupScrollAnimation();
    this.addEventListener('click', this._handleClick, false);
    this.addEventListener('focus', this._handleFocus, false);
  }

  /**
   * Lifecycle event when watched attribute is changed
   *
   * @param {string} name Attribute name
   * @param {any} oldVal Old value of attribute
   * @param {any} newVal New value of attribute
   */
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'expanded':
        this._expandedChanged();
      case 'selected':
        this._selectedChanged();
    }
  }

  /**
   * Handler for `focus` event
   *
   * @private
   */
  _handleFocus() {
    this.selected = true;
  }

  /**
   * Handler for `click` event
   *
   * @private
   */
  _handleClick() {
    if (this.expanded) {
      this.expanded = false;
    } else {
      this.expanded = true;
    }

    // Call expanded changed in case attributeChangedCallback is not triggered - IE10 issue
    this._expandedChanged();
  }

  /**
   * Called when `selected` attribute is changed
   *
   * @private
   */
  _selectedChanged() {
    if (this.selected) {
      this.setAttribute('aria-selected', 'true');
      this.tabIndex = 0;
      this.$emit('tabselected', this);
    } else {
      this.setAttribute('aria-selected', 'false');
      this.tabIndex = -1;
    }
  }

  /**
   * Called when `expanded` attribute is changed
   *
   * @private
   */
  _expandedChanged() {
    if (this.expanded) {
      this.setAttribute('aria-expanded', 'true');
      this.$emit('tabexpanded', this);
    } else {
      this.setAttribute('aria-expanded', 'false');
      this.$emit('tabcollapsed', this);
    }
  }
  /**
   * Scroll animation
   *
   * @private
   */
  _setupScrollAnimation() {
    let id = this.getAttribute('data-id');

    if (id) {
      new ScrollMagic.Scene({
        triggerElement: `[data-id=${this.getAttribute('data-id')}]`,
        duration: 5,
        triggerHook: 0.5,
        reverse: true
      })
        .addTo(controller)
        .on('enter', e => {
          if (
            e.scrollDirection === 'FORWARD' &&
            this.getAttribute('data-js-auto-open') !== 'false'
          ) {
            this.expanded = true;
            this._expandedChanged();
            this.setAttribute('data-js-auto-open', false);
          } else {
            handleNavChange(
              e.currentTarget.triggerElement().getAttribute('data-id')
            );
          }
        });
    }
  }
}

customElements.define(CloverAccordionHeading.is, CloverAccordionHeading);
